import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Customer portal floorplan viewer (CustomerPortal::FloorplansController#show).
//
// Read-only and server-rendered: the graphic is an <img>, every device a dot
// positioned in percent of it, every colour a server-side class. This
// controller only
//
//   * zooms: it widens the canvas box (the <img> and the dot layer inside it)
//     in steps, so the dots keep their percent positions and their size while
//     the viewport scrolls; and
//   * refreshes the status: every `refreshInterval` ms (0 = never; the page
//     sets it only when the login may read device status) it asks the page's
//     own URL for its Turbo Stream — ONE request, whose two `update` actions
//     swap in the dots and the device list with the current status (request.js
//     renders it). A hidden tab skips the tick, and a tick never overlaps the
//     one still in flight.
export default class extends Controller {
  static targets = ["viewport", "canvas"]
  static values = {
    refreshUrl: String,
    refreshInterval: { type: Number, default: 0 },
  }

  static ZOOM_STEPS = [1, 1.5, 2, 3, 4]

  connect() {
    this.zoomIndex = 0
    this.applyZoom()
    if (this.refreshIntervalValue > 0 && this.refreshUrlValue) {
      this.timer = setInterval(() => this.refresh(), this.refreshIntervalValue)
    }
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  zoomIn() {
    this.setZoom(this.zoomIndex + 1)
  }

  zoomOut() {
    this.setZoom(this.zoomIndex - 1)
  }

  resetZoom() {
    this.setZoom(0)
  }

  setZoom(index) {
    const steps = this.constructor.ZOOM_STEPS
    this.zoomIndex = Math.min(Math.max(index, 0), steps.length - 1)
    this.applyZoom()
  }

  applyZoom() {
    if (!this.hasCanvasTarget) return
    const factor = this.constructor.ZOOM_STEPS[this.zoomIndex]
    this.canvasTarget.style.width = `${factor * 100}%`
    this.element.dataset.zoom = String(factor)
  }

  async refresh() {
    if (document.visibilityState === "hidden" || this.inFlight) return
    this.inFlight = true
    try {
      await get(this.refreshUrlValue, { responseKind: "turbo-stream" })
    } catch (_error) {
      // A missed tick is retried on the next one.
    } finally {
      this.inFlight = false
    }
  }
}
